"use client";

import { useState } from "react";
import {
    Check,
    FolderOpen,
    Link2,
    LoaderCircle,
    RefreshCw,
    Upload,
    Video as VideoIcon,
} from "lucide-react";

import LomdaUploadFile from "../shared/lomdaUploadFile";
import {
    LOMDA_DROPZONE_CLASS,
    LOMDA_DROPZONE_DRAG_CLASS,
    LOMDA_DROPZONE_DRAG_MARK,
    LOMDA_DROPZONE_DRAG_WITHIN_CLASS,
} from "../shared/dropzone";
import { useStorage } from "@/features/storage/useStorage";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { t } from "@/lib/localization";
import { MediaAspect, MediaLayoutBlock, MediaPosition } from "@/lib/utils/lomdaV3/types";
import { useLomdaV3Editor } from "../../../store/useLomdaV3Editor";
import { useBlockThumbnail } from "../../../hooks/useBlockEditable";
import { useResolvedInnerGap } from "../../../hooks/useGridGap";
import { useStorageVideoEmbed } from "../../../hooks/useStorageVideoEmbed";
import { isEmbedUrl, toEmbedUrl } from "../../../lib/embedUrl";
import { headingFieldProps } from "../../../lib/ownFonts";
import RichText from "../text/richText";
import { useLomdaLocale } from "../../../hooks/useLomdaLocale";
import { MediaSourceTabs } from "../shared/mediaSourceTabs";
import { IMAGE_UPLOAD, VIDEO_UPLOAD } from "../../../lib/uploadLimits";

const ASPECT_CLASS: Record<MediaAspect, string> = {
    auto: "",
    "16:9": "aspect-video",
    "4:3": "aspect-[4/3]",
    "1:1": "aspect-square",
};

/** Fill mode → CSS object-fit (fill/fit/crop, with cover/contain legacy). */
const OBJECT_FIT: Record<string, string> = {
    fill: "object-fill",
    fit: "object-contain",
    contain: "object-contain",
    crop: "object-cover",
    cover: "object-cover",
};

/** Figma empty-state upload dropzone (icon + title + instructions). */
export const MEDIA_DROPZONE_CLASS = `${LOMDA_DROPZONE_CLASS} flex min-h-[240px] w-full flex-col items-center justify-center gap-4 rounded-[20px] px-6`;

/** One of the three video-source cards in the empty state. */
const VIDEO_SOURCE_CARD = `${LOMDA_DROPZONE_CLASS} relative flex min-h-[200px] flex-col items-center justify-center gap-3 rounded-[20px] px-4`;

interface MediaCellProps {
    block: MediaLayoutBlock;
    index: number;
    position: MediaPosition;
    showCaption: boolean;
    isGrid: boolean;
    editable: boolean;
}

export default function MediaCell({
    block,
    index,
    position,
    showCaption,
    isGrid,
    editable,
}: MediaCellProps) {
    const locale = useLomdaLocale();
    const updateMediaCell = useLomdaV3Editor((s) => s.updateMediaCell);
    const thumbnail = useBlockThumbnail();
    const innerGap = useResolvedInnerGap();
    const [link, setLink] = useState("");
    const [mediaOpen, setMediaOpen] = useState(false);
    const [linkMode, setLinkMode] = useState(false);
    const setExplorerOpen = useStorage((s) => s.setExplorerOpen);

    const { kind, fill, aspect } = block.data;
    const cell = block.data.cells[index];

    const storageVideo = useStorageVideoEmbed(
        cell.videoId,
        kind === "video" && Boolean(cell.videoId) && !thumbnail
    );

    const patchCell = (patch: Parameters<typeof updateMediaCell>[2]) =>
        updateMediaCell(block.id, index, patch);

    const hasMedia = kind === "image" ? Boolean(cell.url) : Boolean(cell.videoId || cell.url);
    const useFrame = kind === "video" || isGrid || aspect !== "auto";
    const frameAspect =
        aspect !== "auto" ? aspect : kind === "video" ? "16:9" : "4:3";
    const frameClass = useFrame ? ASPECT_CLASS[frameAspect] || "aspect-video" : "";

    const submitLink = () => {
        const value = link.trim();
        if (!value) return;
        if (kind === "image") patchCell({ url: value });
        else patchCell({ url: toEmbedUrl(value), videoId: "" });
        setLink("");
        setMediaOpen(false);
        setLinkMode(false);
    };

    // Pick a video from our storage (reuses the storage explorer).
    const pickStorageVideo = () =>
        setExplorerOpen(true, {
            description: t("lomda-v3-editor.media.video-picker"),
            actionsButton: { uploadVideos: true },
            folders: {
                onFolderClickActions: ["navigate-to-folder"],
                menuOptions: { select: false },
            },
            files: {
                initialFilterBy: "videos",
                onFileClickActions: ["select-file"],
                menuOptions: { select: true },
            },
            selectedItemsMenu: {
                mode: "single-file",
                buttons: {
                    cancel: true,
                    custom: {
                        text: t("lomda-v3-editor.media.video-pick"),
                        icon: Check,
                        onClick: (_folders, files) => {
                            const file = files[0] as { id: string } | undefined;
                            if (file) patchCell({ videoId: file.id, url: "" });
                            setExplorerOpen(false);
                        },
                    },
                },
            },
        });

    const uploadControls = (
        <div className="flex flex-col gap-2">
            {kind === "image" ? (
                <LomdaUploadFile
                    reuse="image"
                    selectedUrl={cell.url}
                    options={{
                        ...IMAGE_UPLOAD,
                        className: "h-36 w-full rounded-lg",
                        title: { text: t("lomda-v3-editor.media.upload-image"), keyName: "lomda-v3-editor.media.upload-image" },
                        subtitle: { text: t("lomda-v3-editor.media.drop-hint"), keyName: "lomda-v3-editor.media.drop-hint" },
                    }}
                    onUploadFinished={(file) => {
                        patchCell((c) => ({ url: file.fileUrl, alt: c.alt || file.fileName }));
                        setMediaOpen(false);
                    }}
                />
            ) : (
                <LomdaUploadFile
                    reuse="video"
                    selectedUrl={cell.url}
                    options={{
                        ...VIDEO_UPLOAD,
                        className: "h-36 w-full rounded-lg",
                        title: { text: t("lomda-v3-editor.media.upload-video"), keyName: "lomda-v3-editor.media.upload-video" },
                        subtitle: { text: t("lomda-v3-editor.media.drop-hint"), keyName: "lomda-v3-editor.media.drop-hint" },
                    }}
                    onUploadFinished={(file) => {
                        patchCell({ url: file.fileUrl, videoId: "", name: file.fileName });
                        setMediaOpen(false);
                    }}
                />
            )}
            <div className="flex gap-2">
                <Input
                    value={link}
                    onChange={(e) => setLink(e.target.value)}
                    placeholder={t(kind === "image" ? "lomda-v3-editor.media.image-link" : "lomda-v3-editor.media.video-link")}
                    dir="ltr"
                    className="h-8"
                />
                <Button type="button" size="sm" disabled={!link.trim()} onClick={submitLink}>
                    {t("lomda-v3-editor.media.add")}
                </Button>
            </div>
            {kind === "image" && hasMedia && (
                <Input
                    value={cell.alt}
                    onChange={(e) => patchCell({ alt: e.target.value })}
                    placeholder={t("lomda-v3-editor.media.alt-placeholder")}
                    className="h-8 lomda-v3-text-small"
                />
            )}
        </div>
    );

    const mediaDisplay =
        kind === "image" ? (
            !useFrame ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img src={cell.url} alt={cell.alt} className="h-auto w-full rounded-2xl" />
            ) : (
                <div className={cn("w-full overflow-hidden rounded-2xl", frameClass)}>
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                        src={cell.url}
                        alt={cell.alt}
                        className={cn("h-full w-full", OBJECT_FIT[fill] ?? "object-cover")}
                    />
                </div>
            )
        ) : (
            <div className={cn("w-full overflow-hidden rounded-2xl border bg-black", frameClass)}>
                {thumbnail ? (
                    cell.customThumbnailUrl || cell.thumbnailUrl ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                            src={cell.customThumbnailUrl || cell.thumbnailUrl}
                            alt={cell.alt || cell.name}
                            className="h-full w-full object-cover"
                        />
                    ) : (
                        <div className="flex h-full items-center justify-center text-primary-foreground/60">
                            <VideoIcon className="size-7" />
                        </div>
                    )
                ) : cell.videoId ? (
                    storageVideo.isPending ? (
                        <div className="flex h-full items-center justify-center text-primary-foreground/70">
                            <LoaderCircle className="size-6 animate-spin" />
                        </div>
                    ) : storageVideo.embedUrl ? (
                        <iframe
                            src={storageVideo.embedUrl}
                            title={cell.name}
                            className="h-full w-full"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    ) : (
                        <div className="flex h-full items-center justify-center lomda-v3-text-small text-primary-foreground/70">
                            {t(
                                "lomda-v3-editor.media.video-unavailable",
                                locale
                            )}
                        </div>
                    )
                ) : isEmbedUrl(cell.url) ? (
                    <iframe
                        src={cell.url}
                        title={cell.name}
                        className="h-full w-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                    />
                ) : (
                    // eslint-disable-next-line jsx-a11y/media-has-caption
                    <video
                        src={cell.url}
                        poster={cell.customThumbnailUrl || undefined}
                        controls
                        className={cn("h-full w-full", OBJECT_FIT[fill] ?? "object-cover")}
                    />
                )}
            </div>
        );

    const videoSources = linkMode ? (
        <div className={cn(MEDIA_DROPZONE_CLASS, "gap-3")}>
            <div className="flex w-full max-w-md gap-2">
                <Input
                    value={link}
                    onChange={(e) => setLink(e.target.value)}
                    placeholder={t("lomda-v3-editor.media.video-link")}
                    dir="ltr"
                    className="h-9"
                    autoFocus
                />
                <Button
                    type="button"
                    size="sm"
                    disabled={!link.trim()}
                    onClick={submitLink}
                >
                    {t("lomda-v3-editor.media.add")}
                </Button>
            </div>
            <button
                type="button"
                onClick={() => setLinkMode(false)}
                className="text-sm text-muted-foreground transition hover:text-foreground"
            >
                {t("lomda-v3-editor.media.back")}
            </button>
        </div>
    ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div
                className={cn(
                    VIDEO_SOURCE_CARD,
                    LOMDA_DROPZONE_DRAG_WITHIN_CLASS
                )}
            >
                <Upload className="size-7 text-[hsl(var(--lomda-primary))]" />
                <div className="flex flex-col gap-1">
                    <span className="text-base text-foreground">
                        {t("lomda-v3-editor.media.source-upload-title")}
                    </span>
                    <span className="text-sm text-muted-foreground">
                        {t(
                            "lomda-v3-editor.media.source-upload-desc",
                            undefined,
                            { max: VIDEO_UPLOAD.maxFileSize }
                        )}
                    </span>
                </div>
                <LomdaUploadFile
                    options={{
                        ...VIDEO_UPLOAD,
                        className:
                            "absolute inset-0 h-full w-full cursor-pointer border-0 bg-transparent opacity-0",
                        dragClassName: LOMDA_DROPZONE_DRAG_MARK,
                    }}
                    onUploadFinished={(file) =>
                        patchCell({
                            url: file.fileUrl,
                            videoId: "",
                            name: file.fileName,
                        })
                    }
                />
            </div>
            <button
                type="button"
                onClick={() => setLinkMode(true)}
                className={VIDEO_SOURCE_CARD}
            >
                <Link2 className="size-7 text-[hsl(var(--lomda-primary))]" />
                <div className="flex flex-col gap-1">
                    <span className="text-base text-foreground">
                        {t("lomda-v3-editor.media.source-link-title")}
                    </span>
                    <span className="text-sm text-muted-foreground">
                        {t("lomda-v3-editor.media.source-link-desc")}
                    </span>
                </div>
            </button>
            <button
                type="button"
                onClick={pickStorageVideo}
                className={VIDEO_SOURCE_CARD}
            >
                <FolderOpen className="size-7 text-[hsl(var(--lomda-primary))]" />
                <div className="flex flex-col gap-1">
                    <span className="text-base text-foreground">
                        {t("lomda-v3-editor.media.source-storage-title")}
                    </span>
                    <span className="text-sm text-muted-foreground">
                        {t("lomda-v3-editor.media.source-storage-desc")}
                    </span>
                </div>
            </button>
        </div>
    );

    const media = (() => {
        if (!editable) return hasMedia ? mediaDisplay : null;

        if (!hasMedia) {
            return kind === "image" ? (
                <LomdaUploadFile
                    reuse="image"
                    reuseLayout="canvas"
                    options={{
                        ...IMAGE_UPLOAD,
                        className: MEDIA_DROPZONE_CLASS,
                        dragClassName: LOMDA_DROPZONE_DRAG_CLASS,
                        title: {
                            text: t("lomda-v3-editor.media.upload-image"),
                            keyName: "lomda-v3-editor.media.upload-image",
                        },
                        subtitle: {
                            text: t("lomda-v3-editor.media.drop-hint"),
                            keyName: "lomda-v3-editor.media.drop-hint",
                        },
                    }}
                    onUploadFinished={(file) =>
                        patchCell((c) => ({
                            url: file.fileUrl,
                            alt: c.alt || file.fileName,
                        }))
                    }
                />
            ) : (
                <MediaSourceTabs
                    kind="video"
                    limits={VIDEO_UPLOAD}
                    layout="canvas"
                    onPick={(item) =>
                        patchCell({ url: item.url, videoId: "", name: item.name })
                    }
                    upload={videoSources}
                />
            );
        }

        // Replacing a single cell is driven from the second-level topbar. A grid
        // cell keeps its own control: the topbar has no way to say WHICH cell.
        if (!isGrid) return mediaDisplay;

        return (
            <div className="relative">
                {mediaDisplay}
                <Popover open={mediaOpen} onOpenChange={setMediaOpen}>
                    <PopoverTrigger asChild>
                        <button
                            type="button"
                            className="lomda-v3-chrome absolute end-2 top-2 z-10 flex items-center gap-1.5 rounded-full border bg-card px-2.5 py-1 text-sm text-muted-foreground shadow-sm transition hover:text-primary"
                        >
                            <RefreshCw className="size-3.5" />
                            {t("lomda-v3-editor.media.replace")}
                        </button>
                    </PopoverTrigger>
                    <PopoverContent align="end" className="w-80">
                        {uploadControls}
                    </PopoverContent>
                </Popover>
            </div>
        );
    })();

    const caption = showCaption ? (
        <div
            className="flex flex-1 flex-col justify-center"
            style={{ gap: innerGap }}
        >
            <RichText
                {...headingFieldProps(cell, patchCell)}
                placeholder={t("lomda-v3-editor.text-variant.heading-placeholder")}
                editable={editable}
                useGlobalToolbar
            />
            <RichText
                initialHtml={cell.text}
                placeholder={t("lomda-v3-editor.block.text.placeholder")}
                editable={editable}
                useGlobalToolbar
                onCommit={(text) => patchCell({ text })}
            />
        </div>
    ) : null;

    const isRow = position === "start" || position === "end";
    const mediaFirst = position === "top" || position === "none" || position === "start";
    const mediaEl = (
        <div className={isRow ? "w-full md:flex-1" : "w-full"}>{media}</div>
    );

    return (
        <div
            className={cn(
                "flex gap-4",
                isRow ? "flex-col md:flex-row md:items-center" : "flex-col"
            )}
        >
            {mediaFirst ? (
                <>
                    {mediaEl}
                    {caption}
                </>
            ) : (
                <>
                    {caption}
                    {mediaEl}
                </>
            )}
        </div>
    );
}
